import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom'; // <-- ADDED useLocation
import { Briefcase, MapPin, IndianRupee, LogOut, Sparkles, Clock, ArrowRight, Building2, Search, Filter, CreditCard, Zap, X, Map } from 'lucide-react';

import JobPostingPreview from './Recruiter/JobPosting/JobPostingPreview'; 
import ExternalApplyModal from './ExternalApplyModal';

const CareersDashboard = () => {
  const [jobs, setJobs] = useState([]);
  const [credits, setCredits] = useState(0); 
  const [appliedJobIds, setAppliedJobIds] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const [searchQuery, setSearchQuery] = useState('');
  const [jobFilter, setJobFilter] = useState('all'); 
  const [liveCountry, setLiveCountry] = useState(''); 
  
  const [selectedJob, setSelectedJob] = useState(null); 
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false); 

  const navigate = useNavigate();
  const location = useLocation(); // <-- ADDED HOOK
  
  const applicantId = localStorage.getItem('applicantId');
  const applicantName = localStorage.getItem('applicantName') || 'Applicant';

  const fetchUserData = async () => {
      try {
          const userRes = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/careers-applicant/${applicantId}`);
          setCredits(userRes.data.premiumCredits);
          setAppliedJobIds(userRes.data.appliedJobIds || []); 
      } catch (err) {
          console.error("Failed to fetch user data", err);
      }
  };

  useEffect(() => {
    if (!applicantId) {
      navigate('/');
      return;
    }

    const fetchJobs = async (detectedCountry) => {
      try {
        await fetchUserData(); 

        const jobsRes = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/job-postings?type=public&country=${detectedCountry}`);
        
        const today = new Date();
        const activePublicJobs = jobsRes.data.filter(job => {
            if (!job.isPublicPost) return false;
            if (job.applyByDate) {
                const deadline = new Date(job.applyByDate);
                if (deadline < today) return false;
            }
            return true;
        });

        setJobs(activePublicJobs);
      } catch (err) {
        console.error("Error fetching data:", err);
        setError("Failed to load dashboard data.");
      } finally {
        setLoading(false);
      }
    };

    if ("geolocation" in navigator) {
        navigator.geolocation.getCurrentPosition(
            async (position) => {
                try {
                    const { latitude, longitude } = position.coords;
                    const geoRes = await axios.get(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`);
                    const userCountry = geoRes.data.countryName; 
                    
                    setLiveCountry(userCountry);
                    fetchJobs(userCountry); 
                } catch (geoErr) {
                    console.error("Geocoding failed", geoErr);
                    setError("Failed to determine your country from your location.");
                    setLoading(false);
                }
            }, 
            (error) => {
                setError("Location access is mandatory to view jobs on the Careers Page. Please enable location permissions and refresh.");
                setLoading(false);
            }
        );
    } else {
        setError("Geolocation is not supported by your browser.");
        setLoading(false);
    }
  }, [applicantId, navigate]);

  // --- CATCH THE PENDING JOB AFTER LOGIN ---
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const urlJobId = queryParams.get('jobId');
    
    // Wait for jobs to load, then auto-open the modal
    if (urlJobId && jobs.length > 0) {
      const foundJob = jobs.find(j => String(j.id || j._id) === String(urlJobId));
      if (foundJob) {
        setSelectedJob(foundJob);
        // Clean the URL silently so the popup doesn't re-trigger on refresh
        window.history.replaceState(null, '', '/careers-dashboard'); 
      }
    }
  }, [location.search, jobs]);

  const handleLogout = () => {
    localStorage.clear();
    navigate('/'); 
  };

  const handlePurchaseCredits = async () => {
    try {
        const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/payments/create-checkout-session`, {
            userId: applicantId,
            userType: 'public_applicant', 
            plan: 'premium_credits'
        });
        
        if (response.data.url) {
            window.location.href = response.data.url;
        } else {
            alert("Payment gateway error. Please try again.");
        }
    } catch (err) {
        console.error("Stripe error:", err);
        alert("Failed to initiate payment.");
    }
  };

  const openJobDetails = (job) => setSelectedJob(job);
  const closeJobDetails = () => setSelectedJob(null);

  const handleApplyClick = () => {
    if (selectedJob.isPremiumJob && credits <= 0) {
        alert("You have 0 Premium Credits. Please click the 'Buy' button on the dashboard to purchase credits before applying!");
        return;
    }
    setIsApplyModalOpen(true);
  };

  const filteredJobs = jobs.filter(job => {
      const matchSearch = job.role.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          job.companyName.toLowerCase().includes(searchQuery.toLowerCase());
      
      let matchFilter = true;
      if (jobFilter === 'free') matchFilter = !job.isPremiumJob;
      if (jobFilter === 'premium') matchFilter = job.isPremiumJob;

      return matchSearch && matchFilter;
  });

  if (loading) {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f8fafc' }}>
            <Map size={48} color="#3b82f6" style={{ marginBottom: '16px', animation: 'pulse 2s infinite' }} />
            <h2 style={{ color: '#0f172a' }}>Verifying your location...</h2>
            <p style={{ color: '#64748b' }}>Please allow location access to view jobs available in your region.</p>
        </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f1f5f9', fontFamily: 'sans-serif' }}>
      
      <header style={{ background: '#0f172a', padding: '15px 40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: 'white', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Building2 size={28} color="#3b82f6" />
            <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>Careers Dashboard</h1>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
            
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#1e293b', padding: '6px 12px', borderRadius: '30px', border: '1px solid #334155' }}>
                <span style={{ fontSize: '0.9rem', color: '#cbd5e1', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Zap size={16} color="#f59e0b" fill="#f59e0b" /> Credits: <strong style={{ color: 'white' }}>{credits}</strong>
                </span>
                <button 
                    onClick={handlePurchaseCredits}
                    style={{ background: '#3b82f6', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 'bold', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                    <CreditCard size={14} /> Buy
                </button>
            </div>

            <span style={{ fontSize: '0.95rem', color: '#cbd5e1' }}>Welcome back, <strong>{applicantName}</strong></span>
            
            <button onClick={handleLogout} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.2)', padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: '600' }}>
                <LogOut size={16} /> Logout
            </button>
        </div>
      </header>

      <main style={{ maxWidth: '1200px', margin: '40px auto', padding: '0 20px' }}>
        
        {error ? (
             <div style={{ background: '#fee2e2', color: '#991b1b', padding: '24px', borderRadius: '12px', textAlign: 'center', border: '1px solid #f87171' }}>
                <MapPin size={32} style={{ marginBottom: '12px' }} />
                <h3 style={{ margin: '0 0 8px' }}>Location Access Required</h3>
                <p style={{ margin: 0 }}>{error}</p>
             </div>
        ) : (
            <>
                <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                    <div>
                        <h2 style={{ fontSize: '1.8rem', color: '#0f172a', margin: '0 0 8px' }}>Opportunities in {liveCountry}</h2>
                        <p style={{ color: '#64748b', margin: 0 }}>Discover and apply for elite hospitality roles available in your region.</p>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#dcfce7', color: '#166534', padding: '6px 12px', borderRadius: '20px', fontSize: '0.85rem', fontWeight: 'bold' }}>
                        <MapPin size={14} /> Location Verified
                    </div>
                </div>

                <div style={{ display: 'flex', gap: '16px', marginBottom: '30px', flexWrap: 'wrap' }}>
                    <div style={{ flex: 1, minWidth: '300px', position: 'relative' }}>
                        <Search size={20} color="#94a3b8" style={{ position: 'absolute', left: '16px', top: '14px' }} />
                        <input 
                            type="text" 
                            placeholder="Search roles or companies..." 
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{ width: '100%', padding: '14px 16px 14px 48px', borderRadius: '12px', border: '1px solid #cbd5e1', fontSize: '1rem', outline: 'none', boxSizing: 'border-box' }}
                        />
                    </div>
                    
                    <div style={{ position: 'relative', minWidth: '200px' }}>
                        <Filter size={18} color="#64748b" style={{ position: 'absolute', left: '16px', top: '15px' }} />
                        <select 
                            value={jobFilter} 
                            onChange={(e) => setJobFilter(e.target.value)}
                            style={{ width: '100%', padding: '14px 16px 14px 44px', borderRadius: '12px', border: '1px solid #cbd5e1', fontSize: '1rem', background: 'white', outline: 'none', cursor: 'pointer', appearance: 'none' }}
                        >
                            <option value="all">All Jobs</option>
                            <option value="free">Free Jobs</option>
                            <option value="premium">Premium Jobs</option>
                        </select>
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '24px' }}>
                    {filteredJobs.length === 0 && !loading && (
                        <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '60px', background: 'white', borderRadius: '12px', color: '#64748b' }}>
                            <h3>No jobs available in your region yet.</h3>
                            <p>We couldn't find any opportunities matching {liveCountry} at the moment.</p>
                        </div>
                    )}

                    {filteredJobs.map(job => {
                        const currentJobId = job.id || job._id;
                        const hasApplied = appliedJobIds.includes(currentJobId);

                        return (
                            <div key={currentJobId} style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.05)', border: '1px solid #e2e8f0', display: 'flex', flexDirection: 'column' }}>
                                
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                                    <div>
                                        <h3 style={{ margin: '0 0 6px', fontSize: '1.25rem', color: '#0f172a' }}>{job.role}</h3>
                                        <span style={{ color: '#3b82f6', fontWeight: '600', fontSize: '0.95rem' }}>{job.companyName}</span>
                                    </div>
                                    {job.isPremiumJob && (
                                        <span style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: 'white', padding: '4px 10px', borderRadius: '20px', fontSize: '0.75rem', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                            <Sparkles size={12} /> PREMIUM
                                        </span>
                                    )}
                                </div>

                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '24px', color: '#475569', fontSize: '0.9rem' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><MapPin size={16} color="#94a3b8" /> {job.location || 'Global'}</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Briefcase size={16} color="#94a3b8" /> {job.typeOfWork || 'Full-time'}</div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><IndianRupee size={16} color="#94a3b8" /> {job.ctc ? `${job.ctc} LPA` : 'Disclosed on selection'}</div>
                                </div>

                                <div style={{ marginTop: 'auto', paddingTop: '20px', borderTop: '1px solid #f1f5f9' }}>
                                    <button 
                                        onClick={() => openJobDetails(job)} 
                                        style={{ width: '100%', background: '#e2e8f0', color: '#0f172a', border: 'none', padding: '12px', borderRadius: '8px', fontWeight: 'bold', fontSize: '0.95rem', cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', transition: 'background 0.2s' }}
                                    >
                                        {hasApplied ? 'View Details (Applied)' : 'View Details'} <ArrowRight size={16} />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </>
        )}
      </main>

      {/* --- MODAL 1: JOB DETAILS PREVIEW --- */}
      {selectedJob && !isApplyModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(15, 23, 42, 0.85)', backdropFilter: 'blur(5px)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }} onClick={closeJobDetails}>
          <div style={{ backgroundColor: '#fff', width: '100%', maxWidth: '800px', maxHeight: '90vh', borderRadius: '16px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }} onClick={(e) => e.stopPropagation()}>
            
            <div style={{ padding: '20px 32px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ margin: 0, color: '#0f172a' }}>{selectedJob.role}</h2>
                <p style={{ margin: '4px 0 0', color: '#64748b' }}>{selectedJob.companyName}</p>
              </div>
              <button onClick={closeJobDetails} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }}><X size={24} /></button>
            </div>

            <div style={{ overflowY: 'auto', flex: 1, padding: '20px' }}>
              <JobPostingPreview formData={selectedJob} />
            </div>

            <div style={{ padding: '20px 32px', background: '#f8fafc', borderTop: '1px solid #e2e8f0', textAlign: 'right' }}>
               {appliedJobIds.includes(selectedJob.id || selectedJob._id) ? (
                   <button 
                      disabled
                      style={{ padding: '12px 30px', background: '#10b981', color: 'white', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: 'bold', cursor: 'not-allowed', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                   >
                      ✓ Applied
                   </button>
               ) : (
                   <button 
                      onClick={handleApplyClick}
                      style={{ padding: '12px 30px', background: selectedJob.isPremiumJob ? '#1e293b' : '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontSize: '1rem', fontWeight: 'bold', cursor: 'pointer' }}
                   >
                      {selectedJob.isPremiumJob ? 'Apply (Uses 1 Credit)' : 'Apply Now (Free)'}
                   </button>
               )}
            </div>

          </div>
        </div>
      )}

      {/* --- MODAL 2: ACTUAL APPLY FORM --- */}
      {isApplyModalOpen && selectedJob && (
          <ExternalApplyModal 
              job={selectedJob} 
              onClose={() => {
                  setIsApplyModalOpen(false);
                  setSelectedJob(null);
                  fetchUserData(); 
              }} 
          />
      )}
    </div>
  );
};

export default CareersDashboard;